import { UserCancelledError } from "$root/utils";
import { Text, type DialogBoxSize } from "@mdotm/shadcn/legacy/components";
import type { FormSubmitFn } from "@mdotm/shadcn/mdotm/components";
import type { NodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import type { ReactNode } from "react";
import { spawnYesNoDialog } from "../yes-no-dialog";

export type DeleteDialogCommon = {
	children?: ReactNode;
	header?: string | ReactNode;
	description?: string | ReactNode;
	yesButton?: string | NodeOrFn;
	disableYesButton?: boolean;
	onDeleteAsync: FormSubmitFn<void>;
	noButton?: string | NodeOrFn<{ onClick(): void }>;
	size?: DialogBoxSize;
	entityType?: string;
} & (
	| {
			entityName: string;
			entityNames?: undefined;
	  }
	| {
			entityName?: undefined;
			entityNames: string[];
	  }
);

export type SpawnDeleteDialogParams = DeleteDialogCommon & {
	zIndex?: number;
};

const maxNOfItemsToDisplay = 3;
export async function spawnDeleteDialog(params: SpawnDeleteDialogParams): Promise<void> {
	const items = params.entityName ? [params.entityName.trim()] : (params.entityNames?.map((x) => x.trim()) ?? ["-"]);
	const shouldProceed = await spawnYesNoDialog({
		header: params.header ?? (params.entityType ? `Delete ${params.entityType}` : "Delete"),
		size: "large",
		yesButton: params.yesButton,
		noButton: params.noButton,
		children: params.description ? (
			typeof params.description === "string" ? (
				<Text type="Body/M/Book">{params.description}</Text>
			) : (
				params.description
			)
		) : (
			<>
				<Text type="Body/M/Book">Proceed with the deletion of </Text>

				{items.length === 1 ? (
					<Text type="Body/M/Bold">{items[0]}</Text>
				) : items.length <= maxNOfItemsToDisplay ? (
					<>
						<Text type="Body/M/Bold">{items.slice(0, -1).join(", ")}</Text>
						<Text type="Body/M/Book"> and </Text>
						<Text type="Body/M/Bold">{items.at(-1)}</Text>
					</>
				) : (
					<>
						<Text type="Body/M/Bold">{items.slice(0, maxNOfItemsToDisplay).join(", ")}</Text>
						{items.length === maxNOfItemsToDisplay + 1 && <Text type="Body/M/Book"> and 1 more item</Text>}
						{items.length > maxNOfItemsToDisplay + 1 && (
							<Text type="Body/M/Book"> and {items.length - maxNOfItemsToDisplay} more items</Text>
						)}
					</>
				)}
				<Text type="Body/M/Book">?</Text>
				{params.children}
			</>
		),
		onSubmitAsync: params.onDeleteAsync,
		disableYesButton: params.disableYesButton,
	});
	if (!shouldProceed) {
		throw new UserCancelledError();
	}
}
